import { VISUAL_SETTINGS, type VisualSettingsData } from '@/gameplay/VisualSettings';

/**
 * Instellingenpaneel.
 *
 * De stad was te donker om te spelen, en één schuif in de HUD bleek te weinig: helderheid,
 * schaduwdetail en het lampje op de speler zijn drie verschillende vragen. Ze staan hier als
 * losse schuiven bij elkaar, met de geluidsknop erbij zodat alle instellingen op één plek
 * zitten in plaats van los over de HUD verspreid.
 *
 * Bewust DOM in plaats van geometrie in de scene: hetzelfde patroon als `Hud`, en het schaalt
 * mee met de schermgrootte zonder aan het renderbudget te komen.
 */

export interface SettingsPanelOptions {
  readonly initial: VisualSettingsData;
  readonly onChange: (key: keyof VisualSettingsData, value: number) => void;
  readonly onReset: () => VisualSettingsData;
  readonly onToggleMute?: () => boolean;
}

interface SliderSpec {
  readonly key: keyof VisualSettingsData;
  readonly label: string;
  /** Waarom deze schuif bestaat — staat onder de naam, zodat je niet hoeft te raden. */
  readonly hint: string;
}

const SLIDERS: readonly SliderSpec[] = [
  {
    key: 'brightness',
    label: 'Helderheid',
    hint: 'Belichting van het hele beeld, inclusief neon en verlichte ramen',
  },
  {
    key: 'ambient',
    label: 'Omgevingslicht',
    hint: 'Hoe zichtbaar vlakken zijn waar geen lamp op schijnt',
  },
  {
    key: 'characterLight',
    label: 'Personagelicht',
    hint: 'Het licht dat met Luca meereist',
  },
];

export class SettingsPanel {
  readonly #root: HTMLDivElement;
  readonly #panel: HTMLDivElement;
  readonly #toggle: HTMLButtonElement;
  readonly #muteButton: HTMLButtonElement | null;
  readonly #inputs = new Map<keyof VisualSettingsData, HTMLInputElement>();
  readonly #values = new Map<keyof VisualSettingsData, HTMLSpanElement>();
  #open = false;

  constructor(container: HTMLElement, options: SettingsPanelOptions) {
    this.#root = container.ownerDocument.createElement('div');
    this.#root.className = 'na-settings';
    this.#root.innerHTML = `
      <button class="na-settings-toggle" type="button" aria-expanded="false" aria-controls="na-settings-panel">
        Beeld
      </button>
      <div class="na-settings-panel" id="na-settings-panel" role="group" aria-label="Beeldinstellingen" hidden>
        ${SLIDERS.map((slider) => `
          <label class="na-settings-row" data-key="${slider.key}">
            <span class="na-settings-label">
              ${slider.label}
              <small>${slider.hint}</small>
            </span>
            <input class="na-settings-input" type="range"
                   min="${VISUAL_SETTINGS.min}" max="${VISUAL_SETTINGS.max}" step="1" />
            <span class="na-settings-value"></span>
          </label>
        `).join('')}
        <div class="na-settings-actions">
          <button class="na-settings-reset" type="button">Standaard</button>
          <button class="na-settings-mute" type="button" aria-pressed="false">Geluid aan</button>
        </div>
      </div>
    `;

    installSettingsStyles(container.ownerDocument);
    container.appendChild(this.#root);

    this.#panel = this.#query('.na-settings-panel');
    this.#toggle = this.#query('.na-settings-toggle');
    this.#muteButton = this.#root.querySelector<HTMLButtonElement>('.na-settings-mute');

    for (const slider of SLIDERS) {
      const row = this.#root.querySelector<HTMLLabelElement>(`.na-settings-row[data-key="${slider.key}"]`)!;
      const input = row.querySelector<HTMLInputElement>('.na-settings-input')!;
      const value = row.querySelector<HTMLSpanElement>('.na-settings-value')!;
      this.#inputs.set(slider.key, input);
      this.#values.set(slider.key, value);
      input.addEventListener('input', () => {
        const next = Number(input.value);
        this.#showValue(slider.key, next);
        options.onChange(slider.key, next);
      });
    }

    this.show(options.initial);

    this.#toggle.addEventListener('click', () => this.setOpen(!this.#open));
    this.#query<HTMLButtonElement>('.na-settings-reset').addEventListener('click', () => {
      this.show(options.onReset());
    });
    this.#muteButton?.addEventListener('click', () => {
      const muted = options.onToggleMute?.() ?? false;
      this.#muteButton!.textContent = muted ? 'Geluid uit' : 'Geluid aan';
      this.#muteButton!.setAttribute('aria-pressed', String(muted));
    });
  }

  get open(): boolean {
    return this.#open;
  }

  setOpen(open: boolean): void {
    this.#open = open;
    this.#panel.hidden = !open;
    this.#toggle.setAttribute('aria-expanded', String(open));
  }

  /** Zet alle schuiven op de meegegeven stand — ook gebruikt door de reset-knop. */
  show(data: VisualSettingsData): void {
    for (const slider of SLIDERS) this.#showValue(slider.key, data[slider.key]);
  }

  dispose(): void {
    this.#root.remove();
  }

  #showValue(key: keyof VisualSettingsData, value: number): void {
    const rounded = Math.round(value);
    const input = this.#inputs.get(key);
    const label = this.#values.get(key);
    if (input) input.value = String(rounded);
    if (label) label.textContent = `${rounded}%`;
  }

  #query<T extends HTMLElement>(selector: string): T {
    const found = this.#root.querySelector<T>(selector);
    if (!found) throw new Error(`Instellingenpaneel mist ${selector}`);
    return found;
  }
}

function installSettingsStyles(doc: Document): void {
  if (doc.getElementById('na-settings-styles')) return;
  const style = doc.createElement('style');
  style.id = 'na-settings-styles';
  style.textContent = `
    .na-settings {
      position: absolute;
      right: 18px;
      top: 18px;
      display: grid;
      justify-items: end;
      gap: 8px;
      pointer-events: auto;
      font: 12px ui-monospace, SFMono-Regular, Menlo, monospace;
      color: #e8ddc8;
    }
    .na-settings-toggle,
    .na-settings-reset,
    .na-settings-mute {
      min-height: 32px;
      padding: 7px 12px;
      border: 1px solid rgba(232, 221, 200, 0.32);
      background: rgba(8, 8, 10, 0.68);
      color: #e8ddc8;
      font: inherit;
      cursor: pointer;
    }
    .na-settings-toggle:hover,
    .na-settings-toggle:focus-visible,
    .na-settings-reset:hover,
    .na-settings-mute:hover,
    .na-settings-panel:focus-within {
      border-color: rgba(224, 149, 28, 0.86);
      outline: 2px solid rgba(224, 149, 28, 0.22);
      outline-offset: 2px;
    }
    /* Een author-regel met display verslaat de UA-regel achter het hidden-attribuut, dus
       zonder deze regel staat het paneel altijd open. */
    .na-settings-panel[hidden] {
      display: none;
    }
    .na-settings-panel {
      display: grid;
      gap: 12px;
      width: 280px;
      padding: 14px;
      border: 1px solid rgba(232, 221, 200, 0.32);
      background: rgba(8, 8, 10, 0.86);
    }
    .na-settings-row {
      display: grid;
      grid-template-columns: 1fr 44px;
      grid-template-areas: "label value" "input input";
      gap: 6px 8px;
      align-items: center;
    }
    .na-settings-label {
      grid-area: label;
      display: grid;
      gap: 2px;
    }
    .na-settings-label small {
      color: rgba(232, 221, 200, 0.6);
      font-size: 10px;
      line-height: 1.3;
    }
    .na-settings-value {
      grid-area: value;
      text-align: right;
      color: #e0951c;
    }
    .na-settings-input {
      grid-area: input;
      width: 100%;
      accent-color: #e0951c;
      cursor: pointer;
    }
    .na-settings-actions {
      display: flex;
      gap: 8px;
      justify-content: space-between;
    }
    @media (max-width: 720px) {
      .na-settings { right: 12px; top: 12px; }
      .na-settings-panel { width: min(260px, 68vw); }
    }
  `;
  doc.head.appendChild(style);
}
